import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import usePerformanceActionsApi, { nextStatuses, canSendReminder } from './config';
import ActionFormDrawer from './ActionFormDrawer';
import ReminderDrawer from '../performance-reminders/ReminderDrawer';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';

// Analitik Plan 4 (spec §6.5, §9) — Aksiyonlar: İK bütün aksiyonları görür, açar, düzenler ve iptal eder; sorumlu
// (Gelen Kutum'daki "Performans Aksiyonu" satırından gelir) yalnız kendi aksiyonlarını görür ve durumu ilerletir.
// EmployeesPage aynası DataTable (tek kaydırma, başlık yalnız tabloda); oluşturma/düzenleme GenericFormDrawer'da.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

// Hedef durum → satır menüsü etiketi + OptionsMenu simgesi.
const STATUS_ACTION = {
  planned: { key: 'reopen', type: 'return_to_draft' },
  in_progress: { key: 'start', type: 'resume' },
  done: { key: 'complete', type: 'complete' },
  cancelled: { key: 'cancel', type: 'cancel' },
};

// Sorumlunun kendi aksiyonları (Gelen Kutum hedefi): liste kişinin çalışan kimliğine süzülür (İK de olsa yalnız kendi
// aksiyonları), yeni kayıt düğmesi yok.
export function MyPerformanceActionsPage() {
  return <PerformanceActionsPage mine />;
}

export default function PerformanceActionsPage({ mine = false }) {
  const { t } = useTranslation();
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { user, isHr: isHrSession } = useAuth();
  const isHr = !mine && Boolean(isHrSession);
  const ownerFilter = useMemo(
    () => (mine && user?.employeeId ? { 'ownerEmployeeId[eq]': user.employeeId } : {}),
    [mine, user?.employeeId],
  );

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1, ...ownerFilter });
  const [filterList, setFilterList] = useState([]);
  const [form, setForm] = useState({ open: false, row: null });
  // Faz 2 Plan 2 (spec §5.7) — "Hatırlatmayı Gönder": seçim aksiyonun bağlı kapsamından sunucuda türetilir.
  const [reminder, setReminder] = useState({ open: false, selection: null });

  const {
    fetching, data, pagination, columns: baseColumns, create, update, changeStatus, refresh,
  } = usePerformanceActionsApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams({ ...parseTableParams(tableState, baseColumns), ...ownerFilter });
  }, [baseColumns, ownerFilter]);

  const handleSave = useCallback(async (payload) => {
    const ok = form.row ? await update(form.row.id, payload) : await create(payload);
    return !!ok;
  }, [form.row, create, update]);

  const buildRowActions = useCallback((row) => {
    // Sunucu satır başına yetkiyi döner (canManage = İK); sorumlu yalnız ileri geçişleri görür.
    const manage = !!row.canManage;
    const statusActions = nextStatuses(row, manage).map((s) => ({
      type: STATUS_ACTION[s].type,
      label: t(`performance.action.rowAction.${STATUS_ACTION[s].key}`),
      onClick: () => changeStatus(row.id, s),
    }));
    const edit = manage && row.status !== 'cancelled'
      ? [{ type: 'edit', label: t('performance.action.rowAction.edit'), onClick: () => setForm({ open: true, row }) }]
      : [];
    // Aksiyon otomatik kapanmaz (aynı aksiyon birden çok tur gerektirebilir); son gönderim sütunda görünür.
    const send = canSendReminder(row)
      ? [{
        type: 'send',
        label: t('performance.action.rowAction.sendReminder'),
        onClick: () => setReminder({ open: true, selection: { periodId: row.periodId, sourceActionId: row.id } }),
      }]
      : [];
    return [...send, ...edit, ...statusActions];
  }, [t, changeStatus]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          const actions = buildRowActions(row);
          return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
        },
      },
    },
  ], [baseColumns, data, buildRowActions]);

  return (
    <Container>
      <ActionFormDrawer
        open={form.open}
        row={form.row}
        onClose={() => setForm({ open: false, row: null })}
        onSave={handleSave}
      />
      <ReminderDrawer
        open={reminder.open}
        selection={reminder.selection}
        onClose={() => setReminder({ open: false, selection: null })}
        onSent={refresh}
      />
      <DataTable
        title={mine ? t('performance.action.myPageTitle') : t('performance.action.pageTitle')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleDataStateChange}
        canCreateNewRecord={isHr}
        newButtonLabel={t('performance.action.newAction')}
        onNewButtonClick={() => setForm({ open: true, row: null })}
      />
    </Container>
  );
}
